const {
  BODY_ELEMENTS,
  DOCUMENT_EVENTS,
  LIST_NAMES,
} = require('../analyticsConst');
const impressionsObserver = require('./impressionsObserver');
const analyticUtils = require('../analyticUtils');
const itemListHelpers = require('../helpers/itemListHelpers');
const { onOnce } = require('../../common/utils/domEvents');
const { analyticsEventCenter } = require('../events');
const { impressionItemListStorage, setCurrentList } = require('../stores/itemList');

const analyticsData = {
  impressionsData: {
    impressions: [],
    lastImpressions: [],
    shouldDispatchBatch: false,
  },
  get data() {
    return this.impressionsData;
  },
  set data(obj) {
    this.impressionsData = obj;
  },
};

const observers = {
  crossSelling: impressionsObserver.init(),
  productList: impressionsObserver.init(),
  search: impressionsObserver.init(),
};

/**
 * It disconnects all the observers
 */
function disconnectObservers() {
  observers.productList.disconnect();
  observers.search.disconnect();
  observers.crossSelling.disconnect();
}

/**
 * When user navigates, push sendFinalBatch to dataLayer,
 * disconnect the observers, and remove the event listeners.
 */
function handleNavigation() {
  gaInfo.isPageView = true;
  pushSendFinalBatch();
  disconnectObservers();
}

/**
 * When a user clicks on a product tile, we find the product ID from the data attributes
 * and the list name from the stored impressions data,
 * and then we update the product list in the analytics data stored in sessionStorage
 * @param {Event} e - The event object
 */
function handleProductTileClick(e) {
  const targetElement = e.target.closest(BODY_ELEMENTS.productTileClamp)
    || e.target;
  if (targetElement?.matches(BODY_ELEMENTS.productTileLinks)) {
    const impressionsData = analyticsData.data;
    // Get the closest productTile that contains the product pid or, if navigation happened before
    // and that DOM content is no longer available, search for the product info on the PDP.
    const pid = targetElement.closest(BODY_ELEMENTS.productTile)?.dataset?.pid
      || document.querySelector(BODY_ELEMENTS.productDetail)?.dataset?.pid;
    const productTileElement = targetElement.closest(
      BODY_ELEMENTS.productTileContext
    );
    if (productTileElement?.dataset) {
      const { context } = productTileElement.dataset;
      if (pid && context) {
        trackRecommendedProductImpressions();
      }
    }
    const impressionsProduct = impressionsData.impressions.find(
      (product) => product.dimension32 === pid,
    );
    const list = impressionsProduct && impressionsProduct.list;
    if (pid && list) {
      impressionItemListStorage.setItem(pid, list);
    }
  }
}

/**
 * When the user closes the search results, we need to remove the search results from the impressions
 * list, and then push the last impressions event to the dataLayer
 */
function handleSearchClose() {
  const impressionsData = analyticsData.data;
  // Push send final batch event
  // to send search products to analytics
  pushSendFinalBatch();
  if (!gaInfo.isPageView) {
    // If lastImpressions are stored
    // push them again to dataLayer for product tracking events
    // eslint-disable-next-line camelcase
    if (impressionsData.lastImpressions.length > 0 && google_tag_manager) {
      // eslint-disable-next-line camelcase
      google_tag_manager[gaInfo.gtmId].dataLayer.set('ecommerce.impressions', impressionsData.lastImpressions);
      impressionsData.impressions = impressionsData.lastImpressions;
      impressionsData.lastImpressions = [];
    } else {
      impressionsData.impressions = [];
    }
  } else {
    impressionsData.lastImpressions = [];
    impressionsData.impressions = [];
  }
  analyticsData.data = impressionsData;
}

/**
 * Handle analytics data when search is launched
 */
function handleSearchOpen() {
  const impressionsData = analyticsData.data;
  impressionsData.impressions = [];
  // eslint-disable-next-line camelcase
  if (google_tag_manager) {
    // eslint-disable-next-line camelcase
    impressionsData.lastImpressions = google_tag_manager[gaInfo.gtmId].dataLayer.get('ecommerce.impressions') || [];
    impressionsData.shouldDispatchBatch = impressionsData
      .lastImpressions.length > 0;
  }
  analyticsData.data = impressionsData;
  gaInfo.isPageView = true;
  pushSendFinalBatch();
}

/**
 * It executes the observe method of crossSellingObserver that watches for changes to the product details container.
 * When the product details container changes, the observer will fire a callback function that will send an
 * impression event for each product in the cross-selling section
 */
function observeCrossSellingImpressions() {
  const productDetailsContainer = document.querySelector(
    BODY_ELEMENTS.productDetailsContainer
  );
  if (productDetailsContainer) {
    observers.crossSelling.observe(productDetailsContainer);
  }
}

/**
 * If the `shouldDispatchBatch` flag is set to `true`,
 * then push the `sendFinalBatch` event to the dataLayer
 */
function pushSendFinalBatch() {
  const impressionsData = analyticsData.data;
  if (impressionsData.shouldDispatchBatch) {
    impressionsData.shouldDispatchBatch = false;
    analyticsData.data = impressionsData;
  }
}

/**
 * It removes all the event listeners
 */
function removeEventListeners() {
  document.removeEventListener(DOCUMENT_EVENTS.productSliderReady, observeCrossSellingImpressions);
  document.removeEventListener('click', handleProductTileClick);
  analyticsEventCenter.unsubscribe(
    analyticsEventCenter.EVENTS.productListViewed,
    trackProductListView,
  );
  analyticsEventCenter.unsubscribe(
    analyticsEventCenter.EVENTS.searchHidden,
    handleSearchClose,
  );
  analyticsEventCenter.unsubscribe(
    analyticsEventCenter.EVENTS.searchReturnedResults,
    trackSearch,
  );
  analyticsEventCenter.unsubscribe(
    analyticsEventCenter.EVENTS.userWillNavigateDinamically,
    handleNavigation,
  );
}

/**
 * It takes a recommended product object and builds an impressions array from it
 * with the cross selling products data
 * @param {Object} product full product info
 */
function trackRecommendedProductImpressions() {
  const impressionsData = analyticsData.data;
  impressionsData.shouldDispatchBatch = true;
  analyticsData.data = impressionsData;
  gaInfo.isPageView = false;
}

/**
 * Push product list events page_view_event and ec:ecommerce to dataLayer
 * @param {Object} data - The payload of the event
 * @param {Category} data.category - The category of the PLP
 * @param {FullProduct[]} data.products - The products rendered in PLP
 */
function trackProductListView(data) {
  const productListContainer = document.querySelector(BODY_ELEMENTS.productListContainer);
  observers.productList.observe(productListContainer);
  const impressionsData = analyticsData.data;
  if (data?.category && data?.products?.length > 0) {
    if (gaInfo.isPageView) {
      impressionsData.shouldDispatchBatch = true;
      impressionsData.impressions = [];
    }
    const list = itemListHelpers.buildItemListForCategory(data.category);
    setCurrentList(list);
    const impressionsOffset = impressionsData.impressions.length;
    const lastImpressions = analyticUtils.filterImpressionsArray(data.products, impressionsOffset);
    impressionsData.impressions.push(
      ...analyticUtils.buildImpressionsArray(lastImpressions, list, impressionsOffset),
    );
  }
  analyticsData.data = impressionsData;
  gaInfo.isPageView = false;
}

/**
 * Push search events page_view_event and ec:ecommerce to dataLayer
 * with received data from CustomEvent
 * @param {Object} payload product search data
 * @param {string} payload.search.query search query introduced by user
 * @param {string} payload.search.match search query match
 * @param {string} payload.search.count search number of hits
 * @param {number} payload.search.page search number of page
 * @param {boolean} payload.search.isCached set if search is cached
 * @param {Object[]} payload.products search products
 * @param {boolean} payload.isPageView set if is search first track
 */
function trackSearch(payload) {
  const searchContainer = document.querySelector(BODY_ELEMENTS.searchContainer);
  observers.search.observe(searchContainer);
  const impressionsData = analyticsData.data;
  if (payload?.search?.page === 0 || payload?.search?.isCached) {
    handleSearchOpen();
  }
  gaInfo.isPageView = payload.isPageView || gaInfo.isPageView;
  if (gaInfo.isPageView) {
    impressionsData.impressions = [];
  }
  if (payload?.products?.length > 0) {
    impressionsData.shouldDispatchBatch = true;
    const list = LIST_NAMES.search;
    const impressionsOffset = impressionsData.impressions.length;
    const lastImpressions = analyticUtils.filterImpressionsArray(
      payload.products,
      impressionsOffset,
    );
    impressionsData.impressions.push(
      ...analyticUtils.buildImpressionsArray(
        lastImpressions,
        list,
        impressionsOffset,
        payload.search,
      ),
    );
  }
  analyticsData.data = impressionsData;
  gaInfo.isPageView = false;
}

/**
 * It adds event listeners to the document and window
 */
function init() {
  document.addEventListener(DOCUMENT_EVENTS.productSliderReady, observeCrossSellingImpressions);
  document.addEventListener('click', handleProductTileClick);
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.productListViewed,
    trackProductListView,
  );
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.searchHidden,
    handleSearchClose,
  );
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.searchReturnedResults,
    trackSearch,
  );
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.userWillNavigateDinamically,
    handleNavigation,
  );

  onOnce(window, 'beforeunload', () => {
    pushSendFinalBatch();
    disconnectObservers();
    removeEventListeners();
  });
}

module.exports = {
  init,
};
